import { Point } from "@nivo/line";
import dayjs from "dayjs";
import { ReactNode } from "react";

import { Typo } from "../../../Typo";

type CustomPoint = Point & {
  data: Point["data"] & {
    dayFormatted?: string;
  };
};

type CustomTooltipProps = {
  title: string;
  point: CustomPoint;
  unit?: string;
};

export const LineChartTooltip = ({
  title,
  point,
  unit,
}: CustomTooltipProps): ReactNode => {
  const dayFormatted =
    point.data?.dayFormatted ||
    dayjs(point.data?.xFormatted).format("DD MMM YY");

  return (
    <div
      data-testid="line-chart-tooltip"
      className="flex gap-2 rounded-lg border bg-surface-primary p-3 shadow-sm shadow-stroke-primary"
    >
      {/* Left accent bar */}
      <div
        className="shrink-0 self-stretch rounded"
        style={{
          width: "4px",
          backgroundColor: "rgba(107, 165, 252, 1)",
          borderRadius: "4px",
        }}
      />
      {/* Content section */}
      <div className="flex flex-col gap-2">
        {/* Title and date group */}
        <div className="flex flex-col">
          <Typo element="span" type="labelXS" textColor="text-text-primary">
            {title}
          </Typo>
          <Typo element="span" type="body2XS" textColor="text-text-tertiary">
            {dayFormatted}
          </Typo>
        </div>
        {/* Value section */}
        <Typo element="span" type="labelXS" textColor="text-text-primary">
          {point.data?.yFormatted} {unit}
        </Typo>
      </div>
    </div>
  );
};

export const LineChartTooltipCurrency = ({
  title,
  point,
  value,
}: {
  title: string;
  point: CustomPoint;
  value: string;
}) => {
  const dayFormatted =
    point.data?.dayFormatted ||
    dayjs(point.data?.xFormatted).format("DD MMM YY");

  return (
    <div
      data-testid="line-chart-tooltip-currency"
      className="flex gap-2 rounded-lg border bg-surface-primary p-3 shadow-sm shadow-stroke-primary"
    >
      {/* Left accent bar */}
      <div
        className="shrink-0 self-stretch rounded"
        style={{
          width: "4px",
          backgroundColor: "rgba(107, 165, 252, 1)",
          borderRadius: "4px",
        }}
      />
      {/* Content section */}
      <div className="flex flex-col gap-2">
        {/* Title and date group */}
        <div className="flex flex-col">
          <Typo element="span" type="labelXS" textColor="text-text-primary">
            {title}
          </Typo>
          <Typo element="span" type="body2XS" textColor="text-text-tertiary">
            {dayFormatted}
          </Typo>
        </div>
        {/* Value section */}
        <Typo element="span" type="labelXS" textColor="text-text-primary">
          {value}
        </Typo>
      </div>
    </div>
  );
};

export const LineChartTooltipDisplay = ({
  type,
  title,
  point,
  unit,
  format,
}: {
  type: "usd" | "lots" | "clients";
  title: string;
  point: Point;
  unit?: string;
  format?: (value: number) => string;
}) => {
  const value =
    format && point.data?.yFormatted
      ? format(Number(point.data.yFormatted))
      : "";

  return type === "usd" ? (
    <LineChartTooltipCurrency title={title} point={point} value={value} />
  ) : (
    <LineChartTooltip title={title} point={point} unit={unit} />
  );
};
